import { hot } from 'react-hot-loader/root';
import React from 'react';
import template from 'lodash/template';
import PropTypes from 'prop-types';
import {
  spycraftMixedArrType,
  spycraftBasicArrType,
  spycraftPodcastArrType,
} from '../proptypes/spycraft';
import phrasesType from '../proptypes/phrases';
import SpycraftFeaturedCardDeck from '../components/SpycraftFeaturedCardDeck';
import PagedSpycraftCardDeck from '../components/PagedSpycraftCardDeck';
import PhrasesContext from '../components/contexts/PhrasesContext';
import SpycraftPreviewDeck from '../components/SpycraftPreviewDeck';
import PageSection from '../design-system/PageSection';
import { HeadingHero, HeadingArticle, HeadingPara } from '../design-system/Headings';
import Paragraph from '../design-system/Paragraph';
import PageWrapper from '../design-system/PageWrapper';
import { Divider } from '../design-system/Dividers';
import CloseButton from '../components/CloseButton';
import useCookie from '../hooks/useCookie';

const cookieKey = 'spyscape_intel-welcome';
const cookieValue = 'dismissed';

const IntelPage = ({
  recommendations,
  featured,
  articles,
  podcasts,
  crosswords,
  videos,
  phrases,
  fullName,
  onboardingComplete,
  onboardingChallengesLength,
  onboardingChallengesPlayed,
  nextGamePath,
}) => {
  const [cookie, setCookie] = useCookie(cookieKey, cookieValue);
  const showWelcome = cookie !== cookieValue;
  const recommendedHeader = fullName
    ? template(phrases.recommendationsTitleWithName)({ name: fullName })
    : phrases.recommendationsTitle;

  const handleCrossClick = () => {
    setCookie(cookieValue);
  };

  return (
    <PhrasesContext.Provider value={phrases}>
      <PageWrapper tallNav bgDark>
        { showWelcome && (
          <PageSection bgPrimaryDark>
            <CloseButton handleClick={handleCrossClick} />
            <div className="row">
              <div className="col-lg-10">
                <HeadingHero>{phrases.welcomeTitle}</HeadingHero>
                <Paragraph headline>{phrases.welcomeBody}</Paragraph>
                <Paragraph secondary>{phrases.welcomeSub}</Paragraph>
              </div>
            </div>
          </PageSection>
        )}
        <PageSection bgDarkMid>
          <div className="row">
            <div className="col-md-8 col-lg-6 m-auto text-center">
              <HeadingArticle>{phrases.heroTitle}</HeadingArticle>
              <Paragraph secondary>{phrases.heroIntro}</Paragraph>
            </div>
          </div>
          <Divider myLg />
          <HeadingPara>{recommendedHeader}</HeadingPara>
          <br />
          {onboardingComplete ? (
            <PagedSpycraftCardDeck
              content={recommendations}
              highlight
              showDescription={false}
              itemsPerPage={4}
              cardsPerRow={4}
              nextPageButtonLabel={phrases.moreRecommendationsButtonLabel}
            />
          ) : (
            <SpycraftPreviewDeck
              content={recommendations}
              progressBarLabel={phrases.progressBarLabel}
              onboardingChallengesLength={onboardingChallengesLength}
              onboardingChallengesPlayed={onboardingChallengesPlayed}
              playAllGames={phrases.playAllGames}
              playNextGame={phrases.playNextGame}
              nextGamePath={nextGamePath}
            />
          )}
          <Divider myLg />
          {featured.length === 3 && (
            <>
              <HeadingPara>{phrases.featuredTitle}</HeadingPara>
              <br />
              <SpycraftFeaturedCardDeck content={featured} />
              <Divider />
            </>
          )}
          <HeadingPara>{phrases.articlesTitle}</HeadingPara>
          <br />
          <PagedSpycraftCardDeck
            content={articles}
            itemsPerPage={4}
            nextPageButtonLabel={phrases.moreArticlesButtonLabel}
          />
          <Divider myLg />
          <HeadingPara>{phrases.podcastsTitle}</HeadingPara>
          <br />
          <PagedSpycraftCardDeck
            content={podcasts}
            itemsPerPage={4}
            nextPageButtonLabel={phrases.morePodcastsButtonLabel}
          />
          <Divider myLg />
          <HeadingPara>{phrases.videosTitle}</HeadingPara>
          <br />
          <PagedSpycraftCardDeck
            content={videos}
            itemsPerPage={4}
            nextPageButtonLabel={phrases.moreVideosButtonLabel}
          />
          <Divider myLg />
          <HeadingPara>{phrases.crosswordsTitle}</HeadingPara>
          <br />
          <PagedSpycraftCardDeck
            content={crosswords}
            cardsPerRow={4}
            itemsPerPage={4}
            nextPageButtonLabel={phrases.moreCrosswordsButtonLabel}
          />
        </PageSection>
      </PageWrapper>
    </PhrasesContext.Provider>
  );
};

IntelPage.propTypes = {
  recommendations: spycraftMixedArrType.isRequired,
  featured: spycraftMixedArrType.isRequired,
  articles: spycraftBasicArrType.isRequired,
  podcasts: spycraftPodcastArrType.isRequired,
  videos: spycraftBasicArrType.isRequired,
  crosswords: spycraftBasicArrType.isRequired,
  fullName: PropTypes.string.isRequired,
  phrases: phrasesType.isRequired,
  onboardingComplete: PropTypes.bool.isRequired,
  onboardingChallengesLength: PropTypes.number.isRequired,
  onboardingChallengesPlayed: PropTypes.number.isRequired,
  nextGamePath: PropTypes.string.isRequired,
};

export default hot(IntelPage);
